import { catalog } from '../core/catalog.js';
import { t } from '../core/i18n.js';
import { device } from '../core/device.js';
import { escapeHtml, formatMinutes } from '../core/utils.js';
import { parseAge } from '../core/age.js';
import { firstGenre } from '../core/genres.js';
import { previewMuted, setPreviewMuted, onPreviewMute } from '../core/preview-sound.js';
import { previewEnabled, trailerEmbedUrl, trailerEventsWork, embeddableTrailerId } from './preview.js';
import { img, imageUrl, enhance, titleOf } from './render.js';

/* Vista «En pantalla» de Películas y Series (como el «Cine» de las operadoras): la imagen grande del título elegido de fondo (y,
   pasado un momento, su tráiler en silencio) y abajo una hilera de carátulas. Al recorrerla con el mando el fondo, el título y la
   sinopsis cambian; OK en la elegida abre su ficha; el botón «Reproducir» la pone directamente. */

const SELECT_MS = 350;       // quieto sobre una carátula este rato y se pide su ficha (fondo, sinopsis, tráiler)
const TRAILER_MS = 1800;     // y este rato más y arranca el tráiler
const TRAILER_GIVE_UP_MS = 9000;
const TRAILER_MAX_MS = 60000;
const CHUNK = 60;

const tileHtml = (kind, it) => `<button type="button" class="pst-tile" data-stage-item="${kind}:${escapeHtml(String(it.id))}" aria-label="${escapeHtml(titleOf(it))}">
    <span class="pst-poster"${it.poster ? '' : ` data-poster-need="${kind}:${escapeHtml(String(it.id))}"`}>${img(it.poster, '')}<span class="mi pst-fallback">${kind === 'vod' ? 'movie' : 'tv'}</span></span>
    <span class="pst-title">${escapeHtml(titleOf(it))}</span>
</button>`;

/** Crea la vista dentro de `host` (ocupa toda la pantalla). `onExit()` deja la vista. */
export function createPosterStage(host, { kind, onOpen, onPlay, onExit }) {
    let items = [];
    let shown = 0;
    let sel = null;
    let token = 0;
    let selTimer = null;
    let trailerTimer = null;
    let trailer = null; // { frame, timers, onMessage }
    let alive = true;
    let paused = false;
    const offs = [];
    const more = () => (kind === 'vod' ? catalog.vodInfo : catalog.seriesInfo).bind(catalog);

    host.classList.add('lstage-host');
    document.documentElement.classList.add('stage-open'); // la barra de navegación se esconde mientras esta vista está a la vista
    host.innerHTML = `<div class="lstage pstage" data-lstage>
        <div class="pst-bg" data-pst-bg></div>
        <div class="pst-trailer" data-pst-trailer></div>
        <div class="lstage-shade"></div>
        <button type="button" class="lstage-exit" data-stage-exit aria-label="${escapeHtml(t('live.stageExit'))}"><span class="mi">grid_view</span><span>${escapeHtml(t('live.stageExit'))}</span></button>
        <button type="button" class="lstage-sound" data-lst-sound hidden aria-label="${escapeHtml(t('preview.sound'))}"><span class="mi">volume_off</span></button>
        <div class="lstage-info" data-lst-info></div>
        <div class="lstage-strip" data-lst-strip data-nav-row></div>
    </div>`;
    const stageEl = host.querySelector('[data-lstage]');
    const bgEl = host.querySelector('[data-pst-bg]');
    const trailerBox = host.querySelector('[data-pst-trailer]');
    const soundBtn = host.querySelector('[data-lst-sound]');
    const infoEl = host.querySelector('[data-lst-info]');
    const strip = host.querySelector('[data-lst-strip]');

    const syncStripHeight = () => { stageEl.style.setProperty('--strip-h', strip.offsetHeight + 'px'); }; // el texto y los botones se apoyan justo sobre la hilera, sea cual sea su alto
    if (typeof ResizeObserver !== 'undefined') { const ro = new ResizeObserver(syncStripHeight); ro.observe(strip); offs.push(() => ro.disconnect()); }
    // «Atrás» (mando, Esc, tecla del aparato) deja la vista antes de salir de la página.
    const stageBack = () => { if (paused || !alive) return false; onExit(); return true; };
    if (window.IPTV) window.IPTV.stageBack = stageBack;

    // Con mando, al entrar en la vista el foco va a la hilera (si no, las flechas no hacían nada hasta tocar un canal).
    const focusTile = (tile) => {
        const IP = window.IPTV;
        const tv = (IP && IP.device && IP.device.isTV) || document.documentElement.classList.contains('mode-tv');
        if (!tile || !tv || !IP || !IP.tvnav) return;
        setTimeout(() => { if (alive && !paused && tile.isConnected) IP.tvnav.focus(tile, { scroll: false }); }, 80);
    };
    const keyOf = (it) => kind + ':' + it.id;
    const byKey = (k) => items.find((x) => keyOf(x) === k) || null;
    const tileOf = (it) => strip.querySelector(`[data-stage-item="${window.CSS && CSS.escape ? CSS.escape(keyOf(it)) : keyOf(it)}"]`);
    const markSelected = () => {
        strip.querySelectorAll('.pst-tile.is-selected').forEach((x) => x.classList.remove('is-selected'));
        const tile = sel && tileOf(sel);
        if (tile) tile.classList.add('is-selected');
    };
    const paintStrip = () => {
        shown = Math.min(Math.max(shown, CHUNK), items.length);
        strip.innerHTML = items.slice(0, shown).map((x) => tileHtml(kind, x)).join('');
        enhance(strip);
        markSelected();
        syncStripHeight();
    };
    const appendMore = () => {
        if (shown >= items.length) return;
        const next = items.slice(shown, shown + CHUNK);
        shown += next.length;
        strip.insertAdjacentHTML('beforeend', next.map((x) => tileHtml(kind, x)).join(''));
        enhance(strip);
        markSelected();
    };
    strip.addEventListener('scroll', () => { if (strip.scrollLeft + strip.clientWidth > strip.scrollWidth - 700) appendMore(); }, { passive: true });

    /* ---- Tráiler de fondo ---- */
    const syncSound = () => { soundBtn.querySelector('.mi').textContent = previewMuted() ? 'volume_off' : 'volume_up'; };
    const stopTrailer = () => {
        clearTimeout(trailerTimer);
        trailerTimer = null;
        soundBtn.hidden = true;
        if (!trailer) return;
        const tr = trailer;
        trailer = null;
        tr.timers.forEach(clearTimeout);
        window.removeEventListener('message', tr.onMessage);
        if (tr.resizer) tr.resizer.disconnect();
        tr.frame.classList.remove('is-playing');
        setTimeout(() => { tr.frame.src = 'about:blank'; if (tr.frame.parentNode) tr.frame.parentNode.removeChild(tr.frame); }, 350);
    };
    const command = (frame, func, args = []) => { try { frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func, args }), '*'); } catch (e) { /* aún no cargado */ } };
    const startTrailer = (id) => {
        stopTrailer();
        const frame = document.createElement('iframe');
        frame.className = 'pst-frame';
        frame.setAttribute('allow', 'autoplay; encrypted-media');
        frame.setAttribute('tabindex', '-1');
        frame.setAttribute('aria-hidden', 'true');
        frame.title = 'Trailer';
        frame.src = trailerEmbedUrl(id, 0, previewMuted());
        // El vídeo es 16:9: en una pantalla vertical (móvil) el iframe se agranda para cubrirla entera (se recortan los lados), como un fondo «cover».
        const fitFrame = () => {
            const w = trailerBox.clientWidth;
            const h = trailerBox.clientHeight;
            if (!w || !h) return;
            const k = Math.max(w / 16, h / 9) * 1.12; // un poco de más: se pierden las franjas y el rótulo de YouTube
            frame.style.width = Math.ceil(16 * k) + 'px';
            frame.style.height = Math.ceil(9 * k) + 'px';
            frame.style.left = Math.round((w - 16 * k) / 2) + 'px';
            frame.style.top = Math.round((h - 9 * k) / 2) + 'px';
        };
        const tr = { frame, timers: [], onMessage: null, resizer: null, fitFrame };
        tr.onMessage = (e) => {
            if (e.source !== frame.contentWindow) return;
            let data = e.data;
            if (typeof data === 'string') { try { data = JSON.parse(data); } catch (err) { return; } }
            if (!data || typeof data !== 'object') return;
            if (data.event === 'onError') { stopTrailer(); return; }
            const info = data.info;
            const state = data.event === 'onStateChange' ? info : info && typeof info === 'object' ? info.playerState : undefined;
            if (state === 1 && !frame.classList.contains('is-playing')) {
                tr.timers.forEach(clearTimeout);
                tr.timers = [setTimeout(stopTrailer, TRAILER_MAX_MS)];
                frame.classList.add('is-playing');
                soundBtn.hidden = false;
                syncSound();
                command(frame, 'unloadModule', ['captions']);
            } else if (state === 0) {
                stopTrailer(); // acabó: vuelve la imagen
            }
        };
        window.addEventListener('message', tr.onMessage);
        frame.addEventListener('load', () => {
            try { frame.contentWindow.postMessage(JSON.stringify({ event: 'listening', id: 1, channel: 'widget' }), '*'); } catch (err) { /* nada */ }
            command(frame, 'addEventListener', ['onStateChange']);
            command(frame, 'unloadModule', ['captions']);
        });
        tr.timers.push(setTimeout(stopTrailer, TRAILER_GIVE_UP_MS));
        trailer = tr;
        trailerBox.appendChild(frame);
        fitFrame();
        if (typeof ResizeObserver !== 'undefined') { tr.resizer = new ResizeObserver(fitFrame); tr.resizer.observe(trailerBox); }
    };
    offs.push(onPreviewMute((m) => {
        if (!trailer) return;
        command(trailer.frame, m ? 'mute' : 'unMute');
        if (!m) command(trailer.frame, 'setVolume', [100]);
        syncSound();
    }));

    /* ---- Fondo e información ---- */
    const setBackdrop = (src) => {
        const url = src ? imageUrl(src) : '';
        if (bgEl.dataset.src === url) return;
        bgEl.dataset.src = url;
        if (!url) { bgEl.innerHTML = ''; return; }
        const im = new Image();
        im.className = 'pst-bg-img is-fading';
        im.alt = '';
        im.onload = () => {
            if (bgEl.dataset.src !== url) return;
            const old = Array.from(bgEl.querySelectorAll('.pst-bg-img'));
            bgEl.appendChild(im);
            requestAnimationFrame(() => im.classList.remove('is-fading'));
            setTimeout(() => old.forEach((n) => { if (n.parentNode) n.parentNode.removeChild(n); }), 400);
        };
        im.src = url;
    };
    const renderInfo = (it, info) => {
        const d = info || it;
        const age = parseAge(d.age || it.age);
        const secs = d.durationSecs || 0;
        const bits = [];
        if (age.label) bits.push(`<span class="tag tag-age">${escapeHtml(age.label)}</span>`);
        [d.year || it.year, firstGenre(d.genre || it.genre), secs ? formatMinutes(secs) : (Number(d.episodeRunTime) > 0 ? d.episodeRunTime + ' ' + t('common.minutes') : '')].filter(Boolean).forEach((x) => bits.push(`<span>${escapeHtml(x)}</span>`));
        const rating = Number(d.rating || it.rating) || 0;
        if (rating) bits.push(`<span class="lst-rate"><span class="mi">star</span>${rating.toFixed(1)}</span>`);
        const plot = d.plot || it.plot || '';
        infoEl.innerHTML = `<div class="lst-title">${escapeHtml(titleOf(it))}</div>
            <div class="lst-meta">${bits.join('<span class="lst-sep" aria-hidden="true">·</span>')}</div>
            ${plot ? `<p class="lst-desc">${escapeHtml(plot)}</p>` : ''}
            <div class="lst-actions"><button type="button" class="btn btn-primary" data-stage-play><span class="mi">play_arrow</span>${escapeHtml(t('common.play'))}</button><button type="button" class="btn" data-stage-open><span class="mi">info</span>${escapeHtml(t('detail.moreInfo'))}</button></div>`;
    };

    const select = (it, { now = false } = {}) => {
        if (!it) return;
        const same = sel && keyOf(sel) === keyOf(it);
        sel = it;
        markSelected();
        const have = catalog.peekInfo(kind, it.id);
        renderInfo(it, have);
        setBackdrop((have && have.backdrop) || it.backdrop || it.poster || '');
        if (same) return;
        const my = ++token;
        stopTrailer();
        clearTimeout(selTimer);
        selTimer = setTimeout(async () => {
            let info = have;
            if (!info) { try { info = await more()(it.id); } catch (e) { info = null; } }
            if (!alive || my !== token || !info) return;
            renderInfo(it, info);
            setBackdrop(info.backdrop || it.backdrop || info.poster || it.poster || '');
            if (paused || device.isTV || !previewEnabled() || !trailerEventsWork()) return; // en la tele y Android YouTube no deja incrustar
            const id = embeddableTrailerId(info);
            if (!id) return;
            trailerTimer = setTimeout(() => { if (alive && my === token && !paused) startTrailer(id); }, Math.max(0, TRAILER_MS - SELECT_MS));
        }, now ? 0 : SELECT_MS);
    };

    strip.addEventListener('nav:focus', (e) => {
        const tile = e.target.closest && e.target.closest('[data-stage-item]');
        if (tile && !(sel && keyOf(sel) === tile.getAttribute('data-stage-item'))) select(byKey(tile.getAttribute('data-stage-item')));
    });
    host.addEventListener('click', (e) => {
        const tile = e.target.closest('[data-stage-item]');
        if (tile) {
            const it = byKey(tile.getAttribute('data-stage-item'));
            if (!it) return;
            if (sel && keyOf(sel) === keyOf(it)) onOpen(kind, it.id);
            else select(it, { now: true });
            return;
        }
        if (e.target.closest('[data-stage-exit]')) { onExit(); return; }
        if (e.target.closest('[data-stage-play]')) { if (sel) onPlay(kind, sel.id); return; }
        if (e.target.closest('[data-stage-open]')) { if (sel) onOpen(kind, sel.id); return; }
        if (e.target.closest('[data-lst-sound]')) setPreviewMuted(!previewMuted());
    });

    // Con el reproductor a pantalla completa abierto el tráiler se calla; al cerrarlo, se vuelve a la carátula elegida.
    const player = window.IPTV && window.IPTV.player;
    if (player) {
        offs.push(player.on('open', () => { paused = true; stopTrailer(); }));
        offs.push(player.on('close', () => {
            paused = false;
            const tile = sel && tileOf(sel);
            if (tile && device.isTV && window.IPTV.tvnav) setTimeout(() => window.IPTV.tvnav.focus(tile, { scroll: false }), 100);
        }));
    }

    return {
        setItems(list) {
            items = list;
            shown = 0;
            const keep = sel && byKey(keyOf(sel));
            paintStrip();
            if (keep) { sel = keep; markSelected(); return; }
            const first = items[0];
            select(first, { now: true });
            focusTile(first && tileOf(first));
        },
        pause() { paused = true; stageEl.hidden = true; document.documentElement.classList.remove('stage-open'); stopTrailer(); clearTimeout(selTimer); },
        resume() { if (!paused) return; paused = false; stageEl.hidden = false; document.documentElement.classList.add('stage-open'); if (sel) { const s = sel; sel = null; select(s, { now: true }); } },
        destroy() {
            alive = false;
            clearTimeout(selTimer);
            stopTrailer();
            if (window.IPTV && window.IPTV.stageBack === stageBack) window.IPTV.stageBack = null;
            offs.splice(0).forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } });
            document.documentElement.classList.remove('stage-open');
            host.classList.remove('lstage-host');
            host.innerHTML = '';
        }
    };
}
